import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  platform,
  type PlatformInvitation,
  type PlatformPlan,
  type PlatformTenant,
  type PlatformTenantCreated,
  type PlatformTenantInput,
} from '@/shared/api/resources';
import { formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { PartyToolbar } from '../shared/PartyToolbar';
import { fieldErrorOf } from '../shared/fieldError';
import { opcoesDePlano, planos } from './labels';

/**
 * Empresas da plataforma.
 *
 * A lista traz cadastro e contagens, e nada de dado de negocio: quem administra a
 * plataforma decide plano, situacao e funcionalidades, e nao precisa ler operacao
 * nem apolice de ninguem para isso. Cada consulta fica na trilha da plataforma.
 */

const limite = 25;

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'active', label: 'Ativas' },
  { value: 'suspended', label: 'Suspensas' },
  { value: 'closed', label: 'Encerradas' },
];

export function PlatformTenantsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('');
  const [offset, setOffset] = useState(0);
  const [criando, setCriando] = useState(false);
  const [criada, setCriada] = useState<PlatformTenantCreated | null>(null);

  const consulta = useQuery({
    queryKey: ['platform', 'tenants', { busca, situacao, offset }],
    queryFn: ({ signal }) =>
      platform.listTenants(
        { q: busca, status: situacao, limit: limite, offset },
        signal,
      ),
  });

  const criar = useMutation({
    mutationFn: (input: PlatformTenantInput) => platform.createTenant(input),
    onSuccess: (resultado) => {
      toast.success(`Empresa ${resultado.tenant.name} criada.`);
      setCriando(false);
      void queryClient.invalidateQueries({ queryKey: ['platform'] });
      // O endereco do convite aparece uma vez so. Ir direto para a empresa faria a
      // pessoa perde-lo antes de copiar.
      if (resultado.first_admin_invitation) {
        setCriada(resultado);
      } else {
        void navigate(`/admin/empresas/${resultado.tenant.id}`);
      }
    },
  });

  const colunas: Column<PlatformTenant>[] = [
    {
      key: 'empresa',
      header: 'Empresa',
      render: (empresa) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{empresa.name}</p>
          <p className="truncate font-mono text-2xs text-ink-500">{empresa.slug}</p>
        </div>
      ),
    },
    {
      key: 'plano',
      header: 'Plano',
      render: (empresa) => (
        <Badge tone="brand">{planos[empresa.plan] ?? empresa.plan}</Badge>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (empresa) => <StatusBadge kind="empresa" value={empresa.status} />,
    },
    {
      key: 'pessoas',
      header: 'Pessoas',
      align: 'right',
      hideBelow: 'md',
      render: (empresa) => empresa.member_count,
    },
    {
      key: 'funcionalidades',
      header: 'Funcionalidades',
      align: 'right',
      hideBelow: 'md',
      render: (empresa) => empresa.active_feature_count,
    },
    {
      key: 'criada',
      header: 'Criada em',
      hideBelow: 'lg',
      render: (empresa) => (
        <span className="tabular-nums">{formatDate(empresa.created_at)}</span>
      ),
    },
  ];

  const filtrando = busca !== '' || situacao !== '';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Empresas"
        description="Empresas clientes da plataforma, com plano, situacao e funcionalidades."
        actions={<Button onClick={() => setCriando(true)}>Nova empresa</Button>}
      />

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Nome, identificador, razao social ou CNPJ"
          filter={
            <Select
              label="Situacao"
              className="h-9"
              value={situacao}
              options={situacoes}
              onChange={(evento) => {
                setSituacao(evento.target.value);
                setOffset(0);
              }}
            />
          }
        />

        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar as empresas">
            {consulta.error.message}
          </Alert>
        ) : null}

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(empresa) => empresa.id}
          loading={consulta.isPending}
          onRowClick={(empresa) => void navigate(`/admin/empresas/${empresa.id}`)}
          empty={
            <EmptyState
              icon="briefcase"
              title={filtrando ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa ainda'}
              description={
                filtrando
                  ? 'Ajuste a busca ou a situacao para ver outras empresas.'
                  : 'Crie a primeira empresa cliente da plataforma.'
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {criando ? (
        <NovaEmpresa
          salvando={criar.isPending}
          error={criar.error}
          onCancel={() => {
            criar.reset();
            setCriando(false);
          }}
          onSubmit={(input) => criar.mutate(input)}
        />
      ) : null}

      {criada?.first_admin_invitation ? (
        <ConviteDoPrimeiroAdministrador
          empresa={criada.tenant.name}
          convite={criada.first_admin_invitation}
          onClose={() => {
            const destino = criada.tenant.id;
            setCriada(null);
            void navigate(`/admin/empresas/${destino}`);
          }}
        />
      ) : null}
    </div>
  );
}

function NovaEmpresa({
  salvando,
  error,
  onCancel,
  onSubmit,
}: {
  salvando: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: PlatformTenantInput) => void;
}) {
  const [nome, setNome] = useState('');
  const [razao, setRazao] = useState('');
  const [documento, setDocumento] = useState('');
  const [slug, setSlug] = useState('');
  const [plano, setPlano] = useState<PlatformPlan>('starter');
  const [convidar, setConvidar] = useState(true);
  const [adminNome, setAdminNome] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  const apiError = error instanceof ApiError ? error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const entrada: PlatformTenantInput = {
      slug,
      name: nome,
      legal_name: razao,
      document: documento,
      plan: plano,
    };
    if (convidar) {
      entrada.first_admin = { email: adminEmail, name: adminNome };
    }
    onSubmit(entrada);
  }

  return (
    <Modal
      open
      onClose={onCancel}
      title="Nova empresa"
      description="Papeis vem dos modelos do sistema e funcionalidades vem do plano."
    >
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {error ? (
          <Alert tone="danger" title="Nao foi possivel criar a empresa">
            {error.message}
          </Alert>
        ) : null}

        <fieldset disabled={salvando} className="flex flex-col gap-4">
          <Field
            label="Nome"
            required
            value={nome}
            error={fieldErrorOf(apiError, 'name')}
            onChange={(evento) => setNome(evento.target.value)}
          />
          <Field
            label="Razao social"
            required
            value={razao}
            error={fieldErrorOf(apiError, 'legal_name')}
            onChange={(evento) => setRazao(evento.target.value)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="CNPJ"
              required
              inputMode="numeric"
              value={documento}
              error={fieldErrorOf(apiError, 'document')}
              hint="Com ou sem mascara."
              onChange={(evento) => setDocumento(evento.target.value)}
            />
            <Field
              label="Identificador"
              required
              value={slug}
              error={fieldErrorOf(apiError, 'slug')}
              hint="Minusculas, numeros e hifen. Nao muda depois."
              onChange={(evento) => setSlug(evento.target.value)}
            />
          </div>
          <Select
            label="Plano"
            value={plano}
            options={opcoesDePlano}
            error={fieldErrorOf(apiError, 'plan')}
            onChange={(evento) => setPlano(evento.target.value as PlatformPlan)}
          />

          <div className="rounded-md border border-line p-3">
            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
                checked={convidar}
                onChange={(evento) => setConvidar(evento.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-ink-800">
                  Convidar quem vai administrar a empresa
                </span>
                <span className="block text-2xs text-ink-500">
                  A pessoa recebe o perfil de acesso total da empresa nova e escolhe a
                  propria senha no aceite. A plataforma nao define senha de ninguem.
                </span>
              </span>
            </label>

            {convidar ? (
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field
                  label="Nome de quem administra"
                  required
                  value={adminNome}
                  error={fieldErrorOf(apiError, 'first_admin.name')}
                  onChange={(evento) => setAdminNome(evento.target.value)}
                />
                <Field
                  label="E-mail de quem administra"
                  type="email"
                  required
                  value={adminEmail}
                  error={fieldErrorOf(apiError, 'first_admin.email')}
                  onChange={(evento) => setAdminEmail(evento.target.value)}
                />
              </div>
            ) : null}
          </div>
        </fieldset>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvando}>
            Criar empresa
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Endereco do convite do primeiro administrador, exibido uma unica vez.
 *
 * Enquanto nao houver provedor de e-mail conectado, entregar o endereco e trabalho de
 * quem criou a empresa. A tela diz isso em vez de sugerir que uma mensagem saiu.
 */
function ConviteDoPrimeiroAdministrador({
  empresa,
  convite,
  onClose,
}: {
  empresa: string;
  convite: PlatformInvitation;
  onClose: () => void;
}) {
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(convite.accept_url);
      setCopiado(true);
      toast.success('Endereco copiado.');
    } catch {
      // A area de transferencia pode estar bloqueada pelo navegador. O endereco
      // continua visivel e selecionavel na tela.
      toast.error('Nao foi possivel copiar. Selecione o endereco e copie a mao.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Convite para administrar ${empresa}`}
      description={convite.email}
      footer={<Button onClick={onClose}>Concluir</Button>}
    >
      <div className="flex flex-col gap-4">
        {convite.delivered_by_email ? (
          <Alert tone="success" title="Convite enviado por e-mail">
            A pessoa recebeu o endereco de acesso.
          </Alert>
        ) : (
          <Alert tone="warning" title="Envio automatico nao configurado">
            Nenhum provedor de e-mail esta conectado nesta instalacao. Copie o endereco
            abaixo e envie a pessoa pelo canal que voce ja usa.
          </Alert>
        )}

        <div>
          <p className="text-xs font-semibold text-ink-700">Endereco de acesso</p>
          <p className="mt-1 text-2xs text-ink-500">
            Ele aparece uma vez so, nao fica na trilha e vale ate{' '}
            {formatDate(convite.expires_at)}.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              readOnly
              value={convite.accept_url}
              aria-label="Endereco de acesso do convite"
              onFocus={(evento) => evento.currentTarget.select()}
              className="min-w-0 flex-1 rounded-md border border-line bg-surface-sunken px-3 py-2 font-mono text-2xs text-ink-800"
            />
            <Button variant="secondary" onClick={() => void copiar()}>
              {copiado ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
